import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Search as SearchIcon, Visibility as ViewAsIcon } from '@mui/icons-material';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useAuth } from '../../contexts/AuthContext';
import { UserProfile } from '../../types/database';

interface UserProfileWithEmail extends UserProfile {
  email?: string;
}

export function AdminUserManagement() {
  const supabase = useSupabaseClient();
  const { setViewingAs, user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserProfileWithEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all user profiles (emails are now stored in preferred_email)
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      // Map signup_email to email field for display
      const profilesWithEmail: UserProfileWithEmail[] = (profiles || []).map((profile) => ({
        ...profile,
        email: profile.signup_email,
      }));

      setUsers(profilesWithEmail);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleUserTypeChange = useCallback(
    async (profileId: string, userId: string, newUserType: 'user' | 'manager' | 'admin') => {
      try {
        setUpdating(profileId);
        setError(null);
        setSuccess(null);

        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({ user_type: newUserType })
          .eq('id', profileId);

        if (updateError) throw updateError;

        // Update local state
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === profileId ? { ...user, user_type: newUserType } : user
          )
        );

        setSuccess(`User type updated to ${newUserType}`);
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        console.error('Error updating user type:', err);
        setError(err instanceof Error ? err.message : 'Failed to update user type');
        setTimeout(() => setError(null), 5000);
      } finally {
        setUpdating(null);
      }
    },
    [supabase]
  );

  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.full_name?.toLowerCase().includes(searchLower) ||
      user.signup_email?.toLowerCase().includes(searchLower) ||
      user.preferred_email?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.user_id.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage user types for all accounts. Only administrators can access this section.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TextField
        fullWidth
        placeholder="Search by name, email, or user ID..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Name
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Email
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  User Type
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Created
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm ? 'No users found matching your search' : 'No users found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    {user.full_name || (
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        No name
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.signup_email || user.email || user.preferred_email || (
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        No email
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={user.user_type || 'user'}
                        onChange={(e) =>
                          handleUserTypeChange(
                            user.id,
                            user.user_id,
                            e.target.value as 'user' | 'manager' | 'admin'
                          )
                        }
                        disabled={updating === user.id}
                        sx={{
                          fontWeight: user.user_type === 'admin' ? 600 : 400,
                          color:
                            user.user_type === 'admin'
                              ? 'error.main'
                              : user.user_type === 'manager'
                              ? 'warning.main'
                              : 'text.primary',
                        }}
                      >
                        <MenuItem value="user">User</MenuItem>
                        <MenuItem value="manager">Manager</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(user.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {user.user_id !== currentUser?.id && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setViewingAs(user.user_id);
                          setSuccess(`Now viewing as ${user.full_name || user.signup_email || 'user'}`);
                          setTimeout(() => setSuccess(null), 3000);
                        }}
                        title="View as this user"
                        color="primary"
                      >
                        <ViewAsIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Showing {filteredUsers.length} of {users.length} user{users.length !== 1 ? 's' : ''}
      </Typography>
    </Box>
  );
}

